import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/16/solid';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import useAuth from '@/data/hook/useAuth';
import useSidebar from '@/data/hook/useSidebar';
import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import { AvatarBadge } from '@/components/ui/custom/dataDisplay/AvatarBadge';
import SidebarItem from '../SidebarItem';
import useOptions from './Sidebar.hooks';
import { RewardsView } from '@/components/ui/custom/dataDisplay/RewardsView';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { useMediaQuery } from 'react-responsive';
import { Breakpoints } from '@/utils/Breakpoint';
import useNewUiScope from '@/data/hook/shared/useNewUiScope';
import { twMerge } from 'tailwind-merge';

function SidebarMobile() {
  const { t } = useTranslation('translation', { keyPrefix: 'sideMenu' });
  const isNewUi = useNewUiScope('studentDashboard');
  const { logout } = useAuth();
  const { isOpen, setSidebar } = useSidebar();
  const overlayRef = useRef<HTMLElement>(null);
  const options = useOptions();
  const closeMenu = () => setSidebar(false);
  const { user } = useAuth();
  const handleOverlayClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    const isOverlayComponent = overlayRef.current === e.target;
    if (isOpen && isOverlayComponent) {
      setSidebar(false);
    }
  };

  const isDesktop = useMediaQuery({ minWidth: Breakpoints.lg });

  return (
    <aside
      onClick={handleOverlayClick}
      ref={overlayRef}
      className={`${
        isOpen ? 'fixed lg:hidden w-full h-screen bg-black/30' : 'absolute'
      } z-50 right-0 `}
    >
      <ConditionalRenderer condition={!isDesktop}>
        <label
          data-testid="sidebarMobileButton"
          className={twMerge(
            'swap swap-rotate z-499 absolute top-2 right-6 w-8 h-8',
            isNewUi && 'top-6',
          )}
        >
          <input
            onChange={e => {
              setSidebar(e.target.checked);
            }}
            checked={isOpen}
            type="checkbox"
          />
          <XMarkIcon
            className={twMerge(
              'swap-on text-base-100 size-8',
              isNewUi && 'size-7!',
            )}
          />
          <Bars3Icon
            className={twMerge(
              'swap-off text-primary size-8',
              isNewUi && 'size-7!',
            )}
          />
        </label>
      </ConditionalRenderer>
      <div
        data-testid="sidebarComponentMobile"
        id="sidebar"
        className={`fixed ${
          isOpen ? 'right-0' : '-right-[100vw]'
        } bg-linear-to-t from-primary transition-all duration-400  to-secondary h-full flex z-[498]  flex-col justify-between px-10 pt-[3vh] pb-[5vh] w-[80%] sm:w-[50%]`}
      >
        <div className="flex flex-col gap-[5vh]">
          <div className="flex flex-col gap-2">
            <img
              className="w-full max-w-[144px] self-start"
              src="/images/CtrlPlayFullLogoWhite.png"
              alt="Ctrl+Play"
            />
            <div className="flex justify-center gap-3 lg:hidden">
              <RewardsView />
              <ConditionalRenderer condition={user}>
                <AvatarBadge
                  testId="sidebarProfile"
                  size="14"
                  onClick={closeMenu}
                  user={user}
                />
              </ConditionalRenderer>
            </div>
          </div>
          <div className="flex flex-col gap-[3vh]">
            {options.map(({ condition, ...option }) => {
              if (!option.roles) {
                return (
                  <SidebarItem
                    key={option.title}
                    url={option.url}
                    testId={option.testId + 'Mobile'}
                    isMenuOpen={isOpen}
                    icon={option.icon}
                    text={option.title}
                    condition={condition}
                    onClick={closeMenu}
                  />
                );
              } else {
                return (
                  <ComponentGuard key={option.title} roles={option.roles}>
                    <SidebarItem
                      url={option.url}
                      testId={option.testId + 'Mobile'}
                      condition={condition}
                      isMenuOpen={isOpen}
                      icon={option.icon}
                      text={option.title}
                      onClick={closeMenu}
                    />
                  </ComponentGuard>
                );
              }
            })}
          </div>
        </div>
        <SidebarItem
          url="/login"
          isMenuOpen={isOpen}
          onClick={() => {
            setSidebar(false);
            logout!();
          }}
          icon={<ArrowRightOnRectangleIcon />}
          testId="sidebarLogoutMobile"
          text={t('leave')}
        />
      </div>
    </aside>
  );
}

export default SidebarMobile;
